import React from 'react'
import cn from 'classnames'
import WithTooltip from 'components/shared/WithTooltip'
import {
    getBsvTransactionState,
    getBsvTransactionTooltip,
    BSV_TX_STATES,
} from 'utils/bsvTransactionStatus'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'

type BsvTxState = (typeof BSV_TX_STATES)[keyof typeof BSV_TX_STATES]

type ClassNameValue = string | string[] | undefined

type WrapperProps = React.HTMLAttributes<HTMLSpanElement> &
    React.AnchorHTMLAttributes<HTMLAnchorElement> & {
        [key: `data-${string}`]: unknown
    }

interface BsvTransactionStatusIconProps {
    bsvTx?: Record<string, any> | null
    fallbackTxId?: string | null
    className?: string
    iconClassName?: string
    stateClassNameMap?: Partial<Record<BsvTxState, ClassNameValue>>
    iconClassNameMap?: Partial<Record<BsvTxState, ClassNameValue>>
    disableLink?: boolean
    stopPropagation?: boolean
    onClick?: (ev: React.MouseEvent<HTMLElement>) => void
    onDoubleClick?: (ev: React.MouseEvent<HTMLElement>) => void
    wrapperProps?: WrapperProps
    overrideState?: BsvTxState
}

const normalizeClass = (value?: ClassNameValue) =>
    Array.isArray(value) ? value : value ? [value] : []

const BsvTransactionStatusIcon: React.FC<BsvTransactionStatusIconProps> = ({
    bsvTx,
    fallbackTxId,
    className,
    iconClassName,
    stateClassNameMap,
    iconClassNameMap,
    disableLink = false,
    stopPropagation = true,
    onClick,
    onDoubleClick,
    wrapperProps,
    overrideState,
}) => {
    const txId = bsvTx?.tx_id || fallbackTxId || null
    const computedState = getBsvTransactionState(bsvTx, txId)
    const state = overrideState || computedState
    const tooltip = getBsvTransactionTooltip(bsvTx, txId, state)

    let baseIconClass = 'fa fa-cube'

    switch (state) {
        case BSV_TX_STATES.PENDING:
            baseIconClass = 'fa fa-spinner fa-spin'
            break
        case BSV_TX_STATES.AWAITING_RESUBMISSION:
            baseIconClass = 'fa fa-clock-o'
            break
        case BSV_TX_STATES.FAILED:
            baseIconClass = 'fa fa-times'
            break
        case BSV_TX_STATES.AWAITING_CONFIRMATION:
        case BSV_TX_STATES.CONFIRMED:
        default:
            baseIconClass = 'fa fa-cube'
            break
    }

    const wrapperClassName = cn(className, ...normalizeClass(stateClassNameMap?.[state]))

    const combinedIconClassName = cn(
        baseIconClass,
        iconClassName,
        ...normalizeClass(iconClassNameMap?.[state]),
    )

    const linkEligibleStates = [BSV_TX_STATES.AWAITING_CONFIRMATION, BSV_TX_STATES.CONFIRMED]

    const shouldRenderLink = Boolean(txId) && !disableLink && linkEligibleStates.includes(state)

    const commonHandler = (handler?: (ev: React.MouseEvent<HTMLElement>) => void) =>
        function inner(ev: React.MouseEvent<HTMLElement>) {
            if (stopPropagation) {
                ev.stopPropagation()
            }
            handler?.(ev)
        }

    const handleClick = commonHandler(onClick)
    const handleDoubleClick = commonHandler(onDoubleClick)

    const WrapperTag: React.ElementType = shouldRenderLink ? 'a' : 'span'

    const mergedProps: WrapperProps = {
        ...wrapperProps,
        className: cn(wrapperClassName, wrapperProps?.className),
        'data-tx-id': txId ?? 'NOT_FOUND',
        onClick: event => {
            handleClick(event)
            wrapperProps?.onClick?.(event)
        },
        onDoubleClick: event => {
            handleDoubleClick(event)
            wrapperProps?.onDoubleClick?.(event)
        },
    }

    if (shouldRenderLink) {
        mergedProps.href = bsvTxExplorerUrl(txId)
        mergedProps.target = '_blank'
        mergedProps.rel = 'noopener noreferrer'
    } else {
        delete mergedProps.href
        delete mergedProps.target
        delete mergedProps.rel
    }

    return (
        <WithTooltip tip={tooltip}>
            <WrapperTag {...mergedProps}>
                <i className={combinedIconClassName} />
            </WrapperTag>
        </WithTooltip>
    )
}

export default BsvTransactionStatusIcon
